import {
  ProjectNameTemplateField,
  DEFAULT_PROJECT_NAME_TEMPLATE,
} from "@/data/models/Settings";
import {
  ProjectNameSource,
  ExtendedProjectNameSource,
  LocationInfo,
  ContactPersonInfo,
  Contact,
} from "@/data/models/ProjectDetails";

/*
Many projects carry the same text as project NUMBER and project NAME — the damage address is a common choice
for both — so anything that prints the pair has to collapse it, or the reader sees the address twice. These two
are the one guard for that, mirrored in Cloud (`functions/src/utils/ProjectNameHelper.ts`) so the app and the
PDF report answer it the same way.

Comparison is exact, which is what the ad-hoc guards these replaced did: a number differing only in whitespace
or case is a different number, and normalising here would silently hide it.
*/

/** Whether the number merely repeats the name, so printing both would print it twice. */
export function isProjectNumberSameAsName(
  projectNumber?: string | null,
  projectName?: string | null
): boolean {
  return (projectNumber ?? "") === (projectName ?? "");
}

/**
 * Number and name as one label, collapsed to the name when the number repeats it.
 *
 * Empty parts are dropped, so a project with only one of the two reads as that one.
 */
export function formatProjectNumberAndName(
  projectNumber?: string | null,
  projectName?: string | null,
  separator = " "
): string {
  if (isProjectNumberSameAsName(projectNumber, projectName)) {
    return projectName ?? "";
  }

  return [projectNumber, projectName]
    .filter((part) => !!part && part !== "")
    .join(separator);
}

/**
 * Input data for project name template resolution.
 * Extracted from ProjectDetails to keep the function pure and testable.
 */
export interface ProjectNameInput {
  projectNumber?: string;
  projectNumberIres?: string;
  damageLocationInfo?: Partial<LocationInfo>;
  contactPersons?: Array<Partial<ContactPersonInfo>>;
  contact?: Partial<Contact>;
}

/**
 * Result of project name calculation, including the name and its source classification.
 */
export interface ProjectNameResult {
  name: string;
  source: ProjectNameSource;
  extendedSource: ExtendedProjectNameSource;
}

/**
 * Resolves a single template field to its value from the project data.
 * Returns null if the field has no value.
 */
export function resolveTemplateField(
  field: ProjectNameTemplateField,
  input: ProjectNameInput
): string | null {
  const contactPerson = input.contactPersons?.find(
    (cp) => cp.contactPersonNumber === 1
  );
  const damageLocation = input.damageLocationInfo;

  switch (field) {
    case ProjectNameTemplateField.PROJECT_NUMBER:
      return input.projectNumber || input.projectNumberIres || null;
    case ProjectNameTemplateField.ZIP_CODE:
      return damageLocation?.zipCode || null;
    case ProjectNameTemplateField.CITY:
      return damageLocation?.city || null;
    case ProjectNameTemplateField.STREET:
      return damageLocation?.street || null;
    case ProjectNameTemplateField.HOUSE_NUMBER:
      return damageLocation?.houseNumber || null;
    case ProjectNameTemplateField.NAME:
      return contactPerson?.lastName || input.contact?.lastName || null;
    case ProjectNameTemplateField.COMMA:
      return ",";
    default:
      return null;
  }
}

/**
 * Calculates the project name from a template field list.
 * Resolves each field, skips empty values, joins with spaces,
 * and collapses ` ,` → `,`.
 * Returns null if no non-comma fields have values.
 */
export function calculateProjectNameFromTemplate(
  input: ProjectNameInput,
  template: ProjectNameTemplateField[] = DEFAULT_PROJECT_NAME_TEMPLATE
): string | null {
  const parts: string[] = [];
  for (const field of template) {
    const value = resolveTemplateField(field, input);
    if (value !== null) {
      parts.push(value);
    }
  }

  const nonComma = parts.filter((p) => p !== ",");
  if (nonComma.length === 0) {
    return null;
  }

  const name = parts.join(" ").replace(/ ,/g, ",").replace(/,{2,}/g, ",").replace(/^(,\s*)+/, "").replace(/(\s*,)+$/, "").trim();
  return name || null;
}

/**
 * Legacy CONTACT_PERSON strategy: lastName/city/street joined with "/".
 * Used when no template is configured.
 */
function calculateFromContactPerson(input: ProjectNameInput): string | null {
  const contactPerson = input.contactPersons?.find(
    (cp) => cp.contactPersonNumber === 1
  );
  const name = contactPerson?.lastName || input.contact?.lastName || null;
  const city = input.damageLocationInfo?.city || null;
  const street = input.damageLocationInfo?.street || null;

  const parts = [name, city, street].filter((p): p is string => !!p);

  if (parts.length >= 2) {
    return parts.join("/");
  }
  if (name) {
    return name;
  }
  return null;
}

/**
 * DAMAGE_LOCATION strategy: "street houseNumber, zipCode city".
 * Used as cross-strategy fallback in legacy mode.
 */
function calculateFromDamageLocation(input: ProjectNameInput): string | null {
  const loc = input.damageLocationInfo;
  if (!loc) return null;

  const streetPart = [loc.street, loc.houseNumber].filter((p): p is string => !!p).join(" ");
  const locationPart = [loc.zipCode, loc.city].filter((p): p is string => !!p).join(" ");

  const result = [streetPart, locationPart].filter((p) => !!p).join(", ");
  return result || null;
}

/**
 * Calculates the project name and its source.
 *
 * When a template is configured:
 *   Template → projectNumber → projectNumberIres → null
 *
 * When no template is configured (legacy):
 *   CONTACT_PERSON → DAMAGE_LOCATION → projectNumber → projectNumberIres → null
 */
export function calculateProjectName(
  input: ProjectNameInput,
  template?: ProjectNameTemplateField[]
): ProjectNameResult | null {
  const hasTemplate = template !== undefined && template !== null;

  if (hasTemplate) {
    // Template mode
    const templateName = calculateProjectNameFromTemplate(input, template);
    if (templateName) {
      return {
        name: templateName,
        source: ProjectNameSource.CONTACT_PERSON,
        extendedSource: ExtendedProjectNameSource.TEMPLATE,
      };
    }
  } else {
    // Legacy mode — CONTACT_PERSON strategy
    const contactName = calculateFromContactPerson(input);
    if (contactName) {
      return {
        name: contactName,
        source: ProjectNameSource.CONTACT_PERSON,
        extendedSource: ExtendedProjectNameSource.CONTACT_PERSON,
      };
    }

    // Legacy cross-strategy fallback — DAMAGE_LOCATION
    const damageName = calculateFromDamageLocation(input);
    if (damageName) {
      return {
        name: damageName,
        source: ProjectNameSource.CONTACT_PERSON,
        extendedSource: ExtendedProjectNameSource.DAMAGE_LOCATION,
      };
    }
  }

  // Final fallback chain (both modes)
  if (input.projectNumber) {
    return {
      name: input.projectNumber,
      source: ProjectNameSource.PROJECT_NAME_OFFICE,
      extendedSource: ExtendedProjectNameSource.PROJECT_NAME_OFFICE,
    };
  }

  if (input.projectNumberIres) {
    return {
      name: input.projectNumberIres,
      source: ProjectNameSource.PROJECT_NAME_IRES,
      extendedSource: ExtendedProjectNameSource.PROJECT_NAME_IRES,
    };
  }

  return null;
}
